/**
 * @file The label of the Signify menu's "Add Parts List From Signify" item, which carries the live
 * count of the fetch this table is following. Consumer: `ArTableControls`, through
 * `buildSignifyApiMenuItems`.
 * It is a leaf so that a part write repaints these few words and never the control bar around them
 * — a subscription up in `ArTableControls` is the SYD-2264 regression. antd mounts a menu item's
 * label only once the dropdown has been opened, so an operator who never opens the menu never
 * subscribes at all.
 * The count is shown only for a job started against this table: `uiState.signifyFetchJob` holds one
 * job id per `targetKey`, and this reads that one leaf, so another table's fetch is never this
 * table's and never re-renders it either.
 * Three components, one per guard, because a `useSnapshot` whose result goes unread subscribes to
 * the whole proxy: reading the parts collection above the job guard would re-render this menu on all
 * thousand of the job's part writes before the job document has even arrived.
 */

import { FC, useContext } from 'react';
import { useSnapshot } from 'valtio';
import type { SignifyFetchPart } from '#types';
import { UserContext } from '@/providers/UserContext';
import type { CollectionStore } from '@/store';
import { uiState } from '@/store/uiState';
import {
  createSignifyFetchJobStores,
  deriveSignifyFetchJobProgress,
  signifyFetchJobNoLongerExists,
} from '../use/services';

const LABEL = 'Add Parts List From Signify';

type SignifyFetchJobMenuLabelProps = {
  targetKey: string;
  reason: string | null;
};

type SignifyFetchJobMenuJobProps = Pick<SignifyFetchJobMenuLabelProps, 'reason'> & {
  jobId: string;
  userId: string;
};

type SignifyFetchJobMenuCountProps = Pick<SignifyFetchJobMenuLabelProps, 'reason'> & {
  partsStore: CollectionStore<SignifyFetchPart>;
};

/** The only component subscribed to the parts, and it is mounted only for this table's own job. */
const SignifyFetchJobMenuCount: FC<SignifyFetchJobMenuCountProps> = ({ partsStore, reason }) => {
  const partsSnap = useSnapshot(partsStore.state);

  const progress = deriveSignifyFetchJobProgress(
    null,
    partsSnap.docIds.flatMap((partId) => {
      const entry = partsSnap.docs.get(partId);
      return entry ? [entry.data] : [];
    }),
    Date.now(),
  );
  const label = `${LABEL} · ${progress.done} of ${progress.total} fetched`;

  return <>{reason ? `${label} — ${reason}` : label}</>;
};

/**
 * Split out because the stores may only be read once a job id exists, and React forbids the hooks
 * below running conditionally. A pointer whose job document has not arrived — or has been reaped
 * from under the listener — counts as no fetch, and says the plain label. The reaped case is read
 * through `signifyFetchJobNoLongerExists`, the same predicate the modal routes on, because a menu
 * still counting a job the modal has declared gone is the contradiction the shared spelling closes.
 */
const SignifyFetchJobMenuJob: FC<SignifyFetchJobMenuJobProps> = ({ jobId, userId, reason }) => {
  const { job, parts } = createSignifyFetchJobStores(jobId, userId);
  const jobSnap = useSnapshot(job.state);

  if (signifyFetchJobNoLongerExists(jobSnap)) {
    return <>{reason ? `${LABEL} — ${reason}` : LABEL}</>;
  }

  return <SignifyFetchJobMenuCount partsStore={parts} reason={reason} />;
};

const SignifyFetchJobMenuLabel: FC<SignifyFetchJobMenuLabelProps> = ({ targetKey, reason }) => {
  const activeJobId = useSnapshot(uiState.signifyFetchJob.activeJobIdByTable)[targetKey];
  const { user } = useContext(UserContext);
  const userId = user?.uid;

  if (activeJobId && userId) {
    return <SignifyFetchJobMenuJob jobId={activeJobId} userId={userId} reason={reason} />;
  }

  return <>{reason ? `${LABEL} — ${reason}` : LABEL}</>;
};

export default SignifyFetchJobMenuLabel;
